import React, { useMemo, useState } from "react";
import { Pause, PackageSearch, Play, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtLive = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
export const fmtMins = (m) => `${Math.floor(m / 60)}u ${String(Math.round(m % 60)).padStart(2, "0")}m`;
const FORGOT_HOURS = 10;
// Older logs sometimes stored "Task · Task" (note appended on stop) — collapse to one clean title.
const cleanNote = (n) => Array.from(new Set((n || "").split(" · ").map((s) => s.trim()).filter(Boolean))).join(" · ");

export const MechanicTasks = ({ card, settings, running, liveSec, busy, locked, t, onStart, onStop, onFixEnd }) => {
  const [other, setOther] = useState("");
  const [showOther, setShowOther] = useState(false);
  const [fixTime, setFixTime] = useState("");

  const tiles = useMemo(() => {
    const names = [t("mech_task_diagnosis"), ...(card.services || []).map((s) => s.name), ...((settings?.service_catalog || []).map((c) => c.name))];
    (card.time_logs || []).forEach((l) => l.note && names.push(cleanNote(l.note)));
    return Array.from(new Set(names.filter(Boolean))).slice(0, 16);
  }, [card, settings, t]);

  const perTask = useMemo(() => {
    const m = {};
    for (const l of card.time_logs || []) if (l.stopped_at) { const k = cleanNote(l.note) || t("manualEntry"); m[k] = (m[k] || 0) + (Number(l.minutes) || 0); }
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [card, t]);
  const totalMin = perTask.reduce((s, [, m]) => s + m, 0) + (running ? liveSec / 60 : 0);
  const forgot = running && liveSec > FORGOT_HOURS * 3600;

  return (
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4" data-testid="mechanic-step-clock">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-base md:text-lg flex items-center gap-2"><span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-mono font-bold text-sm">1</span>{t("mech_tasks_title")}</h2>
        <div className={`font-mono text-2xl sm:text-3xl font-black tabular-nums ${running ? "text-emerald-500" : "text-muted-foreground"}`} data-testid="mechanic-timer">{running ? fmtLive(liveSec) : fmtMins(totalMin)}</div>
      </div>
      <p className="text-xs text-muted-foreground">{t("mech_switch_hint")}</p>

      {forgot && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-3 space-y-2" data-testid="mechanic-forgot">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-600"><AlertTriangle className="h-4 w-4" />{t("mech_forgot_title", { h: FORGOT_HOURS })}</div>
          <div className="flex gap-2">
            <Input type="datetime-local" value={fixTime} onChange={(e) => setFixTime(e.target.value)} className="h-11" data-testid="mechanic-fix-end-input" />
            <Button variant="outline" className="h-11 rounded-full" disabled={!fixTime || busy} onClick={() => onFixEnd(new Date(fixTime).toISOString())} data-testid="mechanic-fix-end">{t("mech_fix_end")}</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="mechanic-task-chips">
        {tiles.map((n) => {
          const active = cleanNote(running?.note) === n;
          const done = perTask.find(([k]) => k === n);
          return (
            <button key={n} disabled={busy || locked} onClick={() => (active ? onStop("pause") : onStart(n))}
              className={`relative rounded-xl border p-3 min-h-[64px] text-left transition-colors ${active ? "bg-emerald-500 text-white border-emerald-500 shadow-lg" : "border-border bg-background hover:border-primary hover:text-primary"}`}
              data-testid={`mechanic-task-${n}`}>
              <div className="text-sm font-semibold leading-tight pr-6">{n}</div>
              <div className={`text-[11px] font-mono mt-1 ${active ? "text-white/90" : "text-muted-foreground"}`}>{active ? fmtLive(liveSec) : done ? fmtMins(done[1]) : ""}</div>
              <span className={`absolute top-2.5 right-2.5 h-3 w-3 rounded-full ${active ? "bg-white animate-pulse" : done ? "bg-emerald-500" : "bg-border"}`} />
            </button>
          );
        })}
        <button disabled={busy || locked} onClick={() => setShowOther((v) => !v)} className="rounded-xl border border-dashed border-border p-3 min-h-[64px] text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary flex items-center justify-center gap-1" data-testid="mechanic-task-other"><Plus className="h-4 w-4" />{t("mech_task_other")}</button>
      </div>
      {tiles.length <= 1 && <p className="text-xs text-muted-foreground">{t("mech_no_tasks")}</p>}
      {showOther && (
        <div className="flex gap-2">
          <Input autoFocus value={other} onChange={(e) => setOther(e.target.value)} placeholder={t("taskForTime")} className="h-12 text-base" data-testid="mechanic-task-input" onKeyDown={(e) => { if (e.key === "Enter" && other.trim()) { onStart(other.trim()); setOther(""); setShowOther(false); } }} />
          <Button className="h-12 rounded-full bg-emerald-500 hover:bg-emerald-500/90 text-white px-5" disabled={!other.trim() || busy} onClick={() => { onStart(other.trim()); setOther(""); setShowOther(false); }} data-testid="mechanic-start"><Play className="h-4 w-4 mr-1" />{t("clockIn")}</Button>
        </div>
      )}

      {!locked && card.status !== "completed" && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button variant="outline" className="h-12 rounded-xl" disabled={busy || (!running && card.hold === "pause")} onClick={() => onStop("pause")} data-testid="mechanic-pause"><Pause className="h-4 w-4 mr-2" />{t("mech_pause")}</Button>
          <Button variant="outline" className={`h-12 rounded-xl ${card.hold === "parts" ? "border-orange-500 text-orange-600" : ""}`} disabled={busy || (!running && card.hold === "parts")} onClick={() => onStop("parts")} data-testid="mechanic-wait-parts"><PackageSearch className="h-4 w-4 mr-2" />{t("mech_wait_parts")}</Button>
        </div>
      )}

      {perTask.length > 0 && (
        <div className="rounded-xl bg-muted/40 p-3 text-sm space-y-1" data-testid="mechanic-per-task">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{t("mech_pertask")}</div>
          {perTask.map(([n, m]) => <div key={n} className="flex justify-between gap-3"><span className="truncate">{n}</span><span className="font-mono font-semibold">{fmtMins(m)}</span></div>)}
          <div className="flex justify-between gap-3 border-t border-border pt-1 mt-1 font-bold"><span>{t("mech_total")}</span><span className="font-mono">{fmtMins(totalMin)}</span></div>
        </div>
      )}
    </section>
  );
};
